import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { C3DataSource, C3SourceKind } from '../ports';
import { createDataSource, resolveSourceKind, setActiveDataSource } from '../source';
import { createDataClock, LIVE_REFRESH_INTERVAL_MS, type ClockState, type DataClock } from './clock';

/** Demo reads the invented tenant (the `fixture` source); Live reads the API (`renglo`). */
export type DataMode = 'demo' | 'live';

const modeOfKind = (kind: C3SourceKind): DataMode => (kind === 'fixture' ? 'demo' : 'live');
const kindOfMode = (mode: DataMode): C3SourceKind => (mode === 'demo' ? 'fixture' : 'renglo');

interface DataContextValue {
  source: C3DataSource;
  clock: DataClock;
  mode: DataMode;
  setMode: (mode: DataMode) => void;
}

const DataContext = createContext<DataContextValue | null>(null);

interface C3DataProviderProps {
  /** A source to use as it is, for tests. Then the mode follows it and cannot be switched. */
  source?: C3DataSource;
  /** Where to start. Defaults to what `REACT_APP_DATA_SOURCE` picks (Demo unless it says `renglo`). */
  mode?: DataMode;
  /** Defaults to 30 s. */
  intervalMs?: number;
  /** How often the Threat Feed's records are read again. Defaults to 5 s; 0 turns the fast lane off. */
  liveIntervalMs?: number;
  children: React.ReactNode;
}

/**
 * Holds the query cache, the data source and the one refresh clock for the signed-in console.
 * Mounted under the workspace provider and above the shell; the login does not use it.
 */
export function C3DataProvider({
  source,
  mode: startMode,
  intervalMs,
  liveIntervalMs = LIVE_REFRESH_INTERVAL_MS,
  children,
}: C3DataProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // The clock decides when to read again, not the library.
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            // One attempt, as every reader here made before; the screen says it could not load.
            retry: false,
          },
        },
      })
  );
  const [clock] = useState(() => createDataClock(queryClient, intervalMs, liveIntervalMs));
  const [chosenMode, setChosenMode] = useState<DataMode>(() => startMode ?? modeOfKind(resolveSourceKind()));
  const mode = source ? modeOfKind(source.kind) : chosenMode;
  // Each source is made once, so switching back to Demo finds the same invented tenant.
  const made = useRef<Partial<Record<C3SourceKind, C3DataSource>>>({});
  const active = useMemo(() => {
    const kind = kindOfMode(mode);
    const next = source ?? (made.current[kind] ??= createDataSource(kind));
    // Set while rendering, not in an effect: children read before this component's effects run.
    setActiveDataSource(next);
    return next;
  }, [source, mode]);
  const setMode = useCallback(
    (next: DataMode) => {
      if (source) return;
      // A pause belongs to the data it held; the other source starts running.
      clock.resume();
      setChosenMode(next);
    },
    [source, clock]
  );
  const value = useMemo(() => ({ source: active, clock, mode, setMode }), [active, clock, mode, setMode]);

  useEffect(() => {
    clock.start();
    return () => clock.stop();
  }, [clock]);

  return (
    <QueryClientProvider client={queryClient}>
      <DataContext.Provider value={value}>{children}</DataContext.Provider>
    </QueryClientProvider>
  );
}

function useDataContext(): DataContextValue {
  const value = useContext(DataContext);
  if (!value) throw new Error('Data hooks need a C3DataProvider above them');
  return value;
}

export function useDataSource(): C3DataSource {
  return useDataContext().source;
}

/** @internal The clock itself, for the query hooks. Screens use `useDataClock`. */
export function useClockHandle(): DataClock {
  return useDataContext().clock;
}

export interface DataClockControls extends ClockState {
  pause: () => void;
  resume: () => void;
  /** Shows the latest data and reads again now. Resolves when the reads are done. */
  refreshNow: () => Promise<void>;
}

/** The refresh clock's state and controls: pause, resume, how many updates wait, and "as of". */
export function useDataClock(): DataClockControls {
  const { clock } = useDataContext();
  const state = useSyncExternalStore(clock.subscribe, clock.getState);
  return useMemo(
    () => ({
      ...state,
      pause: clock.pause,
      resume: clock.resume,
      refreshNow: clock.refreshNow,
    }),
    [state, clock]
  );
}

export interface DataModeControls {
  mode: DataMode;
  /** True in Demo. */
  demo: boolean;
  setMode: (mode: DataMode) => void;
  toggle: () => void;
}

/** Whether the console reads the invented tenant (Demo) or the API (Live), and the switch between them. */
export function useDataMode(): DataModeControls {
  const { mode, setMode } = useDataContext();
  return useMemo(
    () => ({ mode, demo: mode === 'demo', setMode, toggle: () => setMode(mode === 'demo' ? 'live' : 'demo') }),
    [mode, setMode]
  );
}
